import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_ROUTES } from './api-routes';
import { map, Observable } from 'rxjs';
import { BillingHistoryGet, Country, Customer, CustomerPortalGet, LocationPaymentPlanGet, Membership, PayableLocationsGet, PaymentMethodsGet, PaymentMethodUpdateGet, PaymentPlan, PromotionCodeAssignRequest, PromotionCodeDetailsGet, PromotionCodeDetailsRequest, PromotionCodeListItem, SubscriptionCostGet, SubscriptionCreate, SubscriptionGet } from '../interfaces/payment-interfaces';
import { DEFAULT_JSON_HEADERS } from './headers.config';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  constructor(private http: HttpClient) { }

  getUserMembership(): Observable<any> {
    return this.http.get<Membership>(
      API_ROUTES.PAYMENT.MEMBERSHIP
    );
  }

  getPaymentPlans(): Observable<any> {
    return this.http.get<PaymentPlan[]>(
      API_ROUTES.PAYMENT.PLANS
    );
  }

  getElectricityInvoice(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<{ data: BillingHistoryGet[] }>(
      API_ROUTES.PAYMENT.BILLING_HISTORY,
      { params: params }
    );
  }

  getCustomer(): Observable<any> {
    return this.http.get<Customer>(
      API_ROUTES.PAYMENT.CUSTOMER
    );
  }

  createCustomer(customerData: Customer): Observable<any> {
    const payload = JSON.stringify(customerData);

    return this.http.put<Customer>(
      API_ROUTES.PAYMENT.CUSTOMER,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateCustomer(customerData: Customer): Observable<any> {
    const payload = JSON.stringify(customerData);

    return this.http.post<Customer>(
      API_ROUTES.PAYMENT.CUSTOMER,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  createSubscription(subscriptionData: SubscriptionCreate): Observable<any> {
    const payload = JSON.stringify(subscriptionData);

    return this.http.post<SubscriptionGet>(
      API_ROUTES.PAYMENT.SUBSCRIPTION_CREATE,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  cancelSubscription(): Observable<any> {
    return this.http.post<any>(
      API_ROUTES.PAYMENT.SUBSCRIPTION_CANCEL,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  resumeSubscription(): Observable<any> {
    return this.http.post<any>(
      API_ROUTES.PAYMENT.SUBSCRIPTION_RESUME,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getActiveSubscription(): Observable<any> {
    return this.http.get<SubscriptionGet>(
      API_ROUTES.PAYMENT.SUBSCRIPTION_ACTIVE
    );
  }

  getCountryCodes(): Observable<Country[]> {
    return this.http.get<Country[]>(
      API_ROUTES.PAYMENT.COUNTRY_CODES
    );
  }

  updatePaymentMethod(): Observable<any> {
    return this.http.post<PaymentMethodUpdateGet>(
      API_ROUTES.PAYMENT.UPDATE_PAYMENT_METHOD,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getPaymentMethods(): Observable<any> {
    return this.http.get<PaymentMethodsGet>(
      API_ROUTES.PAYMENT.PAYMENT_METHODS,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  deletePaymentMethod(payment_method_id: string): Observable<any> {
    return this.http.delete<any>(
      `${API_ROUTES.PAYMENT.PAYMENT_METHOD}/${payment_method_id}`,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  setDefaultPaymentMethod(payment_method_id: string): Observable<any> {
    return this.http.post<any>(
      `${API_ROUTES.PAYMENT.PAYMENT_METHOD}/${payment_method_id}/set-default`,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateSubscriptionPlan(subscriptionData: SubscriptionCreate): Observable<any> {
    const payload = JSON.stringify(subscriptionData);

    return this.http.post<SubscriptionGet>(
      API_ROUTES.PAYMENT.SUBSCRIPTION_UPDATE_PLAN,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getBillingHistory(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.PAYMENT.BILLING_HISTORY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getSubscriptionCost(stripe_subscription_id: string): Observable<any> {
    return this.http.get<SubscriptionCostGet>(
      `${API_ROUTES.PAYMENT.SUBSCRIPTION_COST}/${stripe_subscription_id}`,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getSubscriptionUpdateCost(payment_plan_id: string, pay_yearly: boolean): Observable<any> {
    return this.http.get<SubscriptionCostGet>(
      `${API_ROUTES.PAYMENT.SUBSCRIPTION_UPDATE_PLAN}/${payment_plan_id}/${pay_yearly}/cost`,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getLocationPaymentPlans(): Observable<any> {
    return this.http.get<LocationPaymentPlanGet[]>(
      API_ROUTES.PAYMENT.LOCATION_PLANS
    );
  }

  getLocationPayable(payment_plan_id: string, pay_yearly: boolean): Observable<any> {
    return this.http.get<PayableLocationsGet>(
      `${API_ROUTES.PAYMENT.LOCATION_PAYABLE}/${payment_plan_id}/${pay_yearly}`
    );
  }

  getCustomerPortal(return_url: string): Observable<CustomerPortalGet> {
    const params = new HttpParams().set('return_url', return_url);
    return this.http.get<CustomerPortalGet>(
      API_ROUTES.PAYMENT.CUSTOMER_PORTAL,
      { params: params }
    );
  }

  getPromotionDetails(promotionRequest: PromotionCodeDetailsRequest): Observable<any> {
    const payload = JSON.stringify(promotionRequest);

    return this.http.post<PromotionCodeDetailsGet>(
      API_ROUTES.PAYMENT.PROMOTION_CODE_DETAILS,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  assignPromotionCodeToUser(promotionRequest: PromotionCodeAssignRequest): Observable<any> {
    const payload = JSON.stringify(promotionRequest);

    return this.http.post<Membership>(
      API_ROUTES.PAYMENT.ASSIGN_PROMOTION_CODE,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getPromotionCodesList(): Observable<PromotionCodeListItem[]> {
    return this.http.get<PromotionCodeListItem[]>(
      API_ROUTES.PAYMENT.PROMOTION_CODE_LIST
    );
  }
}
